import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import {
  ProgramInvoiceDataFragment,
  useRecordInvoiceDownloadMutation,
  useRefreshProgramInvoiceUrlsMutation,
} from '../../../../generated/graphql';
import { getGraphQLErrorMessage } from '../../../../lib/errors';
import ToastAlert from '../../../components/ToastAlert';

export type OpenInvoiceKind = 'view' | 'download';

type UseOpenProgramInvoiceOptions = {
  sourcePage: 'payments' | 'patient_profile';
};

const openBlankInvoiceTab = (): Window | null => {
  // Open synchronously so Safari keeps the user-gesture across the await.
  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    return null;
  }

  try {
    newWindow.document.write(
      '<!doctype html><title>Loading invoice…</title><p style="font-family:sans-serif;padding:1rem">Loading invoice…</p>',
    );
    newWindow.document.close();
  } catch {
    // Cross-origin or restricted document access; navigation still works.
  }

  return newWindow;
};

const useOpenProgramInvoice = ({
  sourcePage,
}: UseOpenProgramInvoiceOptions) => {
  const [actionLoadingInvoiceId, setActionLoadingInvoiceId] = useState<
    string | null
  >(null);
  const [refreshProgramInvoiceUrls] = useRefreshProgramInvoiceUrlsMutation();
  const [recordInvoiceDownload] = useRecordInvoiceDownloadMutation();

  const trackInvoiceDownload = useCallback(
    async (programInvoiceId: string) => {
      try {
        await recordInvoiceDownload({
          variables: {
            programInvoiceId,
            sourcePage,
          },
        });
      } catch {
        // Non-blocking: PDF already opened / link shown
      }
    },
    [recordInvoiceDownload, sourcePage],
  );

  const openProgramInvoice = useCallback(
    async (
      programInvoice: ProgramInvoiceDataFragment,
      kind: OpenInvoiceKind,
    ) => {
      const newWindow = openBlankInvoiceTab();

      try {
        setActionLoadingInvoiceId(programInvoice.id);
        const { data } = await refreshProgramInvoiceUrls({
          variables: {
            programInvoiceId: programInvoice.id,
          },
        });
        const refreshed = data?.refreshProgramInvoiceUrls;
        const url =
          kind === 'view'
            ? refreshed?.stripeInvoiceUrl
            : refreshed?.stripeInvoicePdfUrl;

        if (!url) {
          newWindow?.close();
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              message="Invoice URL is unavailable."
              level="error"
            />
          ));
          return;
        }

        if (newWindow && !newWindow.closed) {
          newWindow.location.href = url;
          newWindow.focus();
          if (kind === 'download') {
            await trackInvoiceDownload(programInvoice.id);
          }
        } else {
          const linkLabel = kind === 'view' ? 'Open invoice' : 'Open PDF';
          toast.custom(({ visible }) => (
            <ToastAlert
              isVisible={visible}
              level="warning"
              message={
                <span>
                  Pop-ups are blocked.{' '}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-secondary-125 underline"
                    onClick={() => {
                      if (kind === 'download') {
                        void trackInvoiceDownload(programInvoice.id);
                      }
                    }}
                  >
                    {linkLabel}
                  </a>
                </span>
              }
            />
          ));
        }
      } catch (err) {
        newWindow?.close();
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message={getGraphQLErrorMessage(err)}
            level="error"
          />
        ));
      } finally {
        setActionLoadingInvoiceId(null);
      }
    },
    [refreshProgramInvoiceUrls, trackInvoiceDownload],
  );

  return {
    actionLoadingInvoiceId,
    setActionLoadingInvoiceId,
    openProgramInvoice,
  };
};

export default useOpenProgramInvoice;
